import type { AuditEntry, CaseView, PatientDisplay } from '@chemoflow/contracts';
import { stateOf, type DailyStats } from '@chemoflow/domain';
import { fmt, ymd } from './format.js';

const STATE_LABEL: Record<string, string> = {
  idle: '入力前',
  confirm: '調製待ち',
  change: '調製待ち（変更）',
  stop: '中止',
  start: '調製開始済み',
  made: '調製完了',
  admin: '投与中',
  done: '投与完了',
};

const RX_LABEL: Record<string, string> = { confirm: '調製確定', change: '調製変更', stop: '中止' };

export const PATIENT_HEAD = [
  '患者ID',
  '氏名',
  '診療科',
  '年齢',
  '性別',
  '生年月日',
  'Rp数',
  '状態',
  '連絡種別',
  '理由',
  '確定時刻',
  '調製開始',
  '調製完了',
  '投与開始',
  '投与完了',
  '確定→調製開始(分)',
  '調製開始→投与開始(分)',
];

const diffMin = (a: number | undefined, b: number | undefined) =>
  a && b ? Math.round((b - a) / 60000) : '';

export const patientRows = (cases: readonly CaseView[]): (string | number)[][] =>
  cases.map((c) => [
    c.patient?.patientId ?? c.surrogateKey,
    c.patient?.name ?? '',
    c.patient?.dept ?? '',
    c.patient?.age ?? '',
    c.patient?.sex ?? '',
    c.patient?.birth ?? '',
    c.rpCount,
    STATE_LABEL[stateOf(c)] ?? '',
    c.clerk ? (RX_LABEL[c.clerk.type] ?? '') : '',
    c.clerk?.reason ?? '',
    c.clerk ? fmt(c.clerk.at) : '',
    c.pharm ? fmt(c.pharm.at) : '',
    c.pharmDone ? fmt(c.pharmDone.at) : '',
    c.admin ? fmt(c.admin.at) : '',
    c.adminDone ? fmt(c.adminDone.at) : '',
    diffMin(c.clerk?.at, c.pharm?.at),
    diffMin(c.pharm?.at, c.admin?.at),
  ]);

export const auditRows = (
  entries: readonly AuditEntry[],
  patients: Record<string, PatientDisplay>,
): (string | number)[][] =>
  entries.map((e) => [
    fmt(e.at),
    e.actorName,
    e.actorRole,
    e.surrogateKey ? (patients[e.surrogateKey]?.patientId ?? '') : '',
    e.surrogateKey ? (patients[e.surrogateKey]?.name ?? '') : '',
    e.action,
  ]);

const csvCell = (v: unknown) => {
  const s = String(v ?? '');
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/** Excelでそのまま開けるよう BOM を付ける。 */
export const toCSV = (rows: readonly (readonly unknown[])[]): string =>
  '﻿' + rows.map((r) => r.map(csvCell).join(',')).join('\r\n');

const escapeXml = (v: unknown) =>
  String(v ?? '').replace(
    /[&<>"']/g,
    (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[ch] ?? ch,
  );

/** SpreadsheetML。追加ライブラリなしでExcelが開ける形式。 */
export function toExcelXml(stats: DailyStats, cases: readonly CaseView[]): string {
  const cell = (v: unknown) =>
    `<Cell><Data ss:Type="${typeof v === 'number' ? 'Number' : 'String'}">${escapeXml(v)}</Data></Cell>`;
  const sheet = (name: string, rows: readonly (readonly unknown[])[]) =>
    `<Worksheet ss:Name="${name}"><Table>${rows
      .map((r) => `<Row>${r.map(cell).join('')}</Row>`)
      .join('')}</Table></Worksheet>`;

  const summary: (string | number)[][] = [
    ['項目', '値'],
    ['本日の患者', stats.total],
    ['調製確定', stats.confirm],
    ['調製変更', stats.change],
    ['調製中止', stats.stop],
    ['調製開始（調製件数）', stats.start],
    ['調製完了', stats.made],
    ['投与開始', stats.admin],
    ['投与完了', stats.done],
    ['処方箋（Rp）枚数', stats.rp],
    ['確定→調製開始 平均(分)', stats.toStartMin ?? ''],
    ['調製開始→投与開始 平均(分)', stats.toAdminMin ?? ''],
  ];

  return `<?xml version="1.0" encoding="UTF-8"?><?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet" xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">
${sheet('集計', summary)}${sheet('患者別', [PATIENT_HEAD, ...patientRows(cases)])}</Workbook>`;
}

/** ダウンロード。生成したURLはすぐに解放し、患者情報をメモリに残さない。 */
export function download(name: string, content: string, type: string): void {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

export const exportName = (now: number, suffix = '') =>
  `ケモフロー${suffix ? `_${suffix}` : ''}_${ymd(now)}`;
